import * as React from "react";
import { Table } from "semantic-ui-react";

export interface SauerPreviewViewerProps {
    output: string;
}

interface SauerProdukt {
    FUELLGRAD?: number;
    GUETARTNR?: string;
    GUETARTBEZ?: string;
    KGSOLL?: number;
    LDNR?: number;
    ADR_KLASSE?: string;
    UN_NUMMER?: string;
    ANZLM?: number;
    LBREITE?: number;
    LHOEHE?: number;
}

interface SauerAuftrag {
    Auftraggeber?: { AG_NAME?: string };
    FRACHTZAHLER?: { FZ_NAME?: string };
    BEL_NAME?: string;
    BEL_STR?: string;
    BEL_PLZ?: string;
    BEL_ORT?: string;
    BEL_LKZ?: string;
    BEL_DV?: string;
    BEL_DB?: string;
    BEL_FIX?: number;
    BEL_GEB?: number;
    ENTL_NAME?: string;
    ENTL_STR?: string;
    ENTL_PLZ?: string;
    ENTL_ORT?: string;
    ENTL_LKZ?: string;
    ENTL_DV?: string;
    ENTL_DB?: string;
    ENTL_FIX?: number;
    ENTL_GEB?: number;
    FLOTTENNR?: number;
    LADNRK?: string;
    LADTYP?: string;
    LADTYPTEXT?: string;
    NR_ABS?: string;
    NR_EMPF?: string;
    NR_AG?: string;
    NR_FZ?: string;
    Produkte?: SauerProdukt[];
    REF_NR?: string;
    REF_NR_ABS?: string;
    REF_NR_AG?: string;
    REF_NR_EMPF?: string;
    REF_NR_FZ?: string;
    WOMENTL?: string;
    WOMBEL?: string;
    BEMBEL?: string;
    BEMENTL?: string;
    saufzusatz?: { DV_BED?: string; TRANSP_DV?: string };
    RMELDID?: string;
}

export function tryParseSauer(obj: any): SauerAuftrag | null {
    if (!obj || typeof obj !== "object") return null;
    if (obj.BEL_NAME !== undefined || obj.ENTL_NAME !== undefined || obj.LADNRK !== undefined) {
        return obj as SauerAuftrag;
    }
    return null;
}

export function parseSauerData(output: string): SauerAuftrag | null {
    if (!output) return null;
    try {
        const parsed = JSON.parse(output);

        // RespondHttp-Wrapper: { stringBody: "..." }
        if (parsed.stringBody) {
            try {
                const inner = JSON.parse(parsed.stringBody);
                const result = tryParseSauer(inner);
                if (result) return result;
            } catch { /* stringBody ist kein JSON */ }
        }

        // Voller Converter-Result: { resultFiles: [{base64Content: "..."}] }
        if (Array.isArray(parsed.resultFiles)) {
            for (const file of parsed.resultFiles) {
                if (!file?.base64Content) continue;
                try {
                    const decoded = decodeURIComponent(escape(atob(file.base64Content)));
                    const fileJson = JSON.parse(decoded);
                    // Datei kann selbst ein RespondHttp-Wrapper sein
                    if (fileJson.stringBody) {
                        const inner = JSON.parse(fileJson.stringBody);
                        const result = tryParseSauer(inner);
                        if (result) return result;
                    }
                    const result = tryParseSauer(fileJson);
                    if (result) return result;
                } catch { /* diese Datei überspringen */ }
            }
        }

        // Direktes SauerAuftrag-JSON
        return tryParseSauer(parsed);
    } catch {
        return null;
    }
}

function formatDate(iso?: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    } catch {
        return iso;
    }
}

function formatTime(iso?: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${hh}:${min}`;
    } catch {
        return "";
    }
}


const s: Record<string, React.CSSProperties> = {
    root: {
        fontFamily: "Segoe UI, Tahoma, sans-serif",
        fontSize: 12,
        background: "#f0f0f0",
        padding: 8,
        overflowY: "auto",
        height: "100%",
        boxSizing: "border-box",
    },
    sectionTitle: {
        background: "#d0d8e8",
        fontWeight: "bold",
        padding: "2px 6px",
        marginBottom: 4,
        border: "1px solid #a0a8b8",
        fontSize: 12,
    },
    panel: {
        background: "#ffffff",
        border: "1px solid #c0c0c0",
        padding: 6,
        marginBottom: 6,
    },
    row: {
        display: "flex",
        flexWrap: "wrap" as const,
        gap: 6,
        marginBottom: 4,
    },
    field: {
        display: "flex",
        flexDirection: "column" as const,
    },
    label: {
        fontSize: 10,
        color: "#444",
        marginBottom: 1,
        whiteSpace: "nowrap" as const,
    },
    input: {
        border: "1px solid #999",
        background: "#fff",
        padding: "1px 4px",
        minWidth: 80,
        minHeight: 18,
        fontSize: 12,
        whiteSpace: "pre-wrap" as const,
    },
    inputWide: {
        border: "1px solid #999",
        background: "#fff",
        padding: "1px 4px",
        minWidth: 180,
        minHeight: 18,
        fontSize: 12,
    },
    inputGreen: {
        border: "1px solid #999",
        background: "#00cc00",
        color: "#fff",
        padding: "1px 4px",
        minWidth: 180,
        minHeight: 18,
        fontSize: 12,
        fontWeight: "bold",
    },
    twoCol: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 6,
        marginBottom: 6,
    },
    addressCard: {
        border: "1px solid #c0c0c0",
        background: "#fff",
        padding: 4,
    },
    addressRow: {
        display: "flex",
        gap: 4,
        marginBottom: 3,
        alignItems: "center",
    },
    addressLabel: {
        fontSize: 10,
        color: "#444",
        minWidth: 45,
        whiteSpace: "nowrap" as const,
    },
    addressValue: {
        border: "1px solid #999",
        background: "#00cc00",
        color: "#fff",
        padding: "1px 4px",
        flex: 1,
        fontSize: 12,
        fontWeight: "bold",
        minHeight: 16,
    },
    addressValuePlain: {
        border: "1px solid #999",
        background: "#fff",
        padding: "1px 4px",
        flex: 1,
        fontSize: 12,
        minHeight: 16,
    },
    badge: {
        background: "#4a7aaa",
        color: "#fff",
        fontWeight: "bold",
        fontSize: 10,
        padding: "1px 6px",
        marginRight: 4,
        display: "inline-block",
    },
    noData: {
        color: "#888",
        fontStyle: "italic",
        padding: 16,
    },
};

function Field({ label, value, wide, green }: { label: string; value?: string | number; wide?: boolean; green?: boolean }) {
    return (
        <div style={s.field}>
            <div style={s.label}>{label}</div>
            <div style={green ? s.inputGreen : wide ? s.inputWide : s.input}>{value ?? ""}</div>
        </div>
    );
}

function AddressSection({ title, badge, stationNr, auftraggeber, name, street, plz, ort, lkz, datumVon, datumBis, fix, womelden, bemerkung }:
    { title: string; badge: string; stationNr?: string; auftraggeber?: string; name?: string; street?: string; plz?: string; ort?: string; lkz?: string; datumVon?: string; datumBis?: string; fix?: number; womelden?: string; bemerkung?: string }) {
    return (
        <div>
            <div style={s.sectionTitle}>{title}</div>
            <div style={s.panel}>
                <div style={s.row}>
                    <Field label="Datum von" value={formatDate(datumVon)} />
                    <Field label="Uhrzeit von" value={formatTime(datumVon)} />
                    <Field label="Datum bis" value={formatDate(datumBis)} />
                    <Field label="Uhrzeit bis" value={formatTime(datumBis)} />
                    <Field label="Fix" value={fix === 1 ? "✓" : ""} />
                </div>
                <div style={s.addressCard}>
                    <div style={s.addressRow}>
                        <span style={s.badge}>{badge}</span>
                        {stationNr && <div style={{ ...s.addressValuePlain, flex: "none", minWidth: 80, fontWeight: "bold" }}>{stationNr}</div>}
                        <div style={{ ...s.addressValuePlain, fontWeight: "bold" }}>{name ?? ""}</div>
                    </div>
                    {auftraggeber && (
                        <div style={s.addressRow}>
                            <span style={s.addressLabel}>Auftraggeber:</span>
                            <div style={s.addressValuePlain}>{auftraggeber}</div>
                        </div>
                    )}
                    <div style={s.addressRow}>
                        <span style={s.addressLabel}>Straße:</span>
                        <div style={s.addressValue}>{street ?? ""}</div>
                    </div>
                    <div style={s.addressRow}>
                        <span style={s.addressLabel}>Lkz:</span>
                        <div style={{ ...s.addressValue, flex: "none", minWidth: 30 }}>{lkz ?? ""}</div>
                        <span style={s.addressLabel}>Plz:</span>
                        <div style={s.addressValue}>{plz ?? ""}</div>
                    </div>
                    <div style={s.addressRow}>
                        <span style={s.addressLabel}>{badge === "ABS." ? "B.Ort:" : "E.Ort:"}</span>
                        <div style={s.addressValue}>{ort ?? ""}</div>
                    </div>
                    <div style={s.addressRow}>
                        <span style={s.addressLabel}>Wo melden?:</span>
                        <div style={{ ...s.addressValuePlain, background: "#fffbe6" }}>{womelden ?? ""}</div>
                    </div>
                    <div style={s.addressRow}>
                        <span style={s.addressLabel}>Bemerkung:</span>
                        <div style={{ ...s.addressValuePlain, minHeight: 32, whiteSpace: "pre-wrap" }}>{bemerkung ?? ""}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function SauerPreviewViewer({ output }: SauerPreviewViewerProps) {
    const data = React.useMemo(() => parseSauerData(output), [output]);

    if (!data) {
        return <div style={s.noData}>Kein Sauer-Auftrag erkannt. Das Ausgabeformat muss ein Sauer-JSON sein (direkt oder als HTTP-Response mit stringBody).</div>;
    }

    return (
        <div style={s.root}>
            <div style={s.sectionTitle}>Bearbeitung — Auftrag</div>

            <div style={s.panel}>
                <div style={s.row}>
                    <Field label="Auftraggeber Nr. (NR_AG)" value={data.NR_AG} />
                    <Field label="Auftraggeber Name" value={data.Auftraggeber?.AG_NAME} wide />
                    <Field label="Frachtzahler Nr. (NR_FZ)" value={data.NR_FZ} />
                    <Field label="Frachtzahler Name" value={data.FRACHTZAHLER?.FZ_NAME} wide />
                    <Field label="Ladungstyp" value={data.LADTYP ? `${data.LADTYP} – ${data.LADTYPTEXT ?? ""}` : data.LADTYPTEXT} wide />
                    <Field label="Flotte Nr." value={data.FLOTTENNR} />
                </div>
                <div style={s.row}>
                    <Field label="Lade Nr. (LADNRK)" value={data.LADNRK} wide />
                    <Field label="Ref.Nr. (REF_NR)" value={data.REF_NR} wide />
                    <Field label="Transportart" value={data.saufzusatz?.DV_BED} />
                </div>
                <div style={s.row}>
                    <Field label="Kundenauftragsnummer (REF_NR_AG)" value={data.REF_NR_AG} wide />
                    <Field label="Transportbelegnummer (REF_NR_FZ)" value={data.REF_NR_FZ} wide />
                </div>
                <div style={s.row}>
                    <Field label="Abholnummer (REF_NR_ABS)" value={data.REF_NR_ABS} wide />
                    <Field label="Bestellnummer (REF_NR_EMPF)" value={data.REF_NR_EMPF} wide />
                </div>
                {data.RMELDID && (
                    <div style={s.row}>
                        <Field label="Zusammenfassungs-ID (RMELDID)" value={data.RMELDID} wide />
                    </div>
                )}
            </div>

            <div style={s.twoCol}>
                <AddressSection
                    title="Abwicklung Beladung"
                    badge="ABS."
                    stationNr={data.NR_ABS}
                    auftraggeber={data.Auftraggeber?.AG_NAME}
                    name={data.BEL_NAME}
                    street={data.BEL_STR}
                    plz={data.BEL_PLZ}
                    ort={data.BEL_ORT}
                    lkz={data.BEL_LKZ}
                    datumVon={data.BEL_DV}
                    datumBis={data.BEL_DB}
                    fix={data.BEL_FIX}
                    womelden={data.WOMBEL}
                    bemerkung={data.BEMBEL}
                />
                <AddressSection
                    title="Abwicklung Entladung"
                    badge="EMPF."
                    stationNr={data.NR_EMPF}
                    name={data.ENTL_NAME}
                    street={data.ENTL_STR}
                    plz={data.ENTL_PLZ}
                    ort={data.ENTL_ORT}
                    lkz={data.ENTL_LKZ}
                    datumVon={data.ENTL_DV}
                    datumBis={data.ENTL_DB}
                    fix={data.ENTL_FIX}
                    womelden={data.WOMENTL}
                    bemerkung={data.BEMENTL}
                />
            </div>

            {data.Produkte && data.Produkte.length > 0 && (
                <>
                    <div style={s.sectionTitle}>Auftrag Produktzeile</div>
                    <div style={{ overflowX: "auto" }}>
                        <Table celled compact size="small" style={{ fontSize: 11 }}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>#</Table.HeaderCell>
                                    <Table.HeaderCell>Gt.Nr.</Table.HeaderCell>
                                    <Table.HeaderCell>Bezeichnung</Table.HeaderCell>
                                    <Table.HeaderCell>Gewicht kg (KGSOLL)</Table.HeaderCell>
                                    <Table.HeaderCell>Füllgrad kg/l (FUELLGRAD)</Table.HeaderCell>
                                    <Table.HeaderCell>ADR Klasse</Table.HeaderCell>
                                    <Table.HeaderCell>UN-Nr</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {data.Produkte.map((p, i) => (
                                    <Table.Row key={i}>
                                        <Table.Cell>{p.LDNR ?? i + 1}</Table.Cell>
                                        <Table.Cell>{p.GUETARTNR ?? ""}</Table.Cell>
                                        <Table.Cell>{p.GUETARTBEZ ?? ""}</Table.Cell>
                                        <Table.Cell>{p.KGSOLL != null ? p.KGSOLL.toLocaleString("de-DE") : ""}</Table.Cell>
                                        <Table.Cell>{p.FUELLGRAD != null ? p.FUELLGRAD.toLocaleString("de-DE") : ""}</Table.Cell>
                                        <Table.Cell>{p.ADR_KLASSE ?? ""}</Table.Cell>
                                        <Table.Cell>{p.UN_NUMMER ?? ""}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );
}
